/**
 * Logged-in experience for `/provider-signup`.
 *
 * Anonymous users see the full application. Authenticated users never see
 * create-account fields — they are redirected, shown status, or offered an
 * upgrade apply that attaches a provider application to their session.
 */

export const PROVIDER_SIGNUP_STAFF_ROLES = [
  'coordinator',
  'training_manager',
  'admin',
] as const;

export type ProviderSignupStaffRole = (typeof PROVIDER_SIGNUP_STAFF_ROLES)[number];

export type ProviderSignupGateMode =
  | 'anonymous'
  | 'loading'
  | 'staff'
  | 'profile_incomplete'
  | 'provider_status'
  | 'upgrade';

export type ProviderSignupGateInput = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  /** Primary `users.role` string when known. */
  role?: string | null;
  /**
   * Normalized role names from `user.roles` (and aliases). Prefer this over
   * primary `role` alone — coordinators often keep a non-staff primary role.
   */
  roles?: readonly string[] | null;
  providerAccessLoading: boolean;
  profileIncomplete: boolean;
  applicationStatus: 'pending' | 'approved' | 'rejected' | null;
};

export function isProviderSignupStaffRole(
  role: string | null | undefined,
): role is ProviderSignupStaffRole {
  return (
    role === 'coordinator' ||
    role === 'training_manager' ||
    role === 'admin'
  );
}

/** True when primary role or any held role is coordinator / TM / admin. */
export function userHoldsProviderSignupStaffRole(input: {
  role?: string | null;
  roles?: readonly string[] | null;
}): boolean {
  if (isProviderSignupStaffRole(input.role)) return true;
  if (!input.roles || input.roles.length === 0) return false;
  for (const name of input.roles) {
    const normalized = String(name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    // training_manager ↔ manager alias used elsewhere in dashboard RBAC
    if (normalized === 'manager') {
      if (isProviderSignupStaffRole('training_manager')) return true;
      continue;
    }
    if (isProviderSignupStaffRole(normalized)) return true;
  }
  return false;
}

/**
 * Resolve what `/provider-signup` should render for the current session.
 * Staff short-circuits before provider status (coordinators can create events
 * without a training_providers row).
 */
export function resolveProviderSignupGate(
  input: ProviderSignupGateInput,
): ProviderSignupGateMode {
  if (input.isInitializing) return 'loading';
  if (!input.isAuthenticated) return 'anonymous';
  if (userHoldsProviderSignupStaffRole({ role: input.role, roles: input.roles })) {
    return 'staff';
  }
  if (input.providerAccessLoading) return 'loading';
  if (input.profileIncomplete) return 'profile_incomplete';
  if (
    input.applicationStatus === 'pending' ||
    input.applicationStatus === 'approved' ||
    input.applicationStatus === 'rejected'
  ) {
    return 'provider_status';
  }
  return 'upgrade';
}

/** Prefer an explicit staff role from the held-roles list for dashboard deep links. */
export function resolveProviderSignupStaffRoleForDashboard(input: {
  role?: string | null;
  roles?: readonly string[] | null;
}): ProviderSignupStaffRole | null {
  if (isProviderSignupStaffRole(input.role)) return input.role;
  if (!input.roles) return null;
  for (const preferred of PROVIDER_SIGNUP_STAFF_ROLES) {
    for (const name of input.roles) {
      const normalized = String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
      if (normalized === 'manager' && preferred === 'training_manager') {
        return 'training_manager';
      }
      if (normalized === preferred) return preferred;
    }
  }
  return null;
}

/** Dashboard deep link for staff who already host training. */
export function getProviderSignupStaffDashboardPath(
  role: string | null | undefined,
): string {
  switch (role) {
    case 'training_manager':
      return '/dashboard?role=training_manager&tmTab=events';
    case 'admin':
      return '/dashboard?role=admin';
    case 'coordinator':
      return '/dashboard?role=coordinator&coordinatorTab=events';
    default: {
      return '/dashboard?role=coordinator&coordinatorTab=events';
    }
  }
}
